import { Fragment, useState, useEffect, useRef, useMemo } from "react";
import Box from '@mui/material/Box';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import { useTranslation, withTranslation } from "react-i18next";
import {cloneDeep, isEmpty, isNull, isEqual } from "lodash";

import debug from "@plannel/utils/debug-util";
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import {DownloadButton, UploadButton} from "@plannel/components/ActionIconButton";
import { AgGridReact } from 'ag-grid-react';
import DefaultGridSetting from "./aggrid/DefaultGridSetting";
import {Checkbox, FormControlLabel, Menu, MenuItem} from "@mui/material";
import dateUtils, { DEFAULT_DATE_FORMAT } from "@plannel/utils/date-util";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BaseConfigModal, { MODAL_SECTION_LABEL_SX } from "@plannel/components/modal/BaseConfigModal";
import { FS } from "@plannel/utils/font-util";

/**
 * @param {object} props service, params, columnDefs, setExcelData
 * @param {service} service (required)
 * @param {object} params searchCode
 * @param {object} columnDefs columnDefs (required for grids that need to be personalized)
 * @param {function} setExcelData (required: pivot grid, parameter: headers, results)
 * @param {function} headerFormatter (parameter: value)
 * @param {function} hasUnsavedChanges true / false
 * @param {number} totalRows (required: pagination pivot grid)
 * @param {boolean} isCsv (default: false)
 * @param {object} noteDefs (columnDefs)
 */

const CSV_DOWNLOAD_MAX_SIZE = 150000;
const DP_PLAN_VS_ACTUAL_EXPORT_LIMIT = 100000;
// TranslationColumns -> 헤더 항목 이외의 프론트에서 translation 필요한 컬럼
const ExcelExportButton = ({ title, service, params = {}, columnDefs, setExcelData, headerFormatter, dataType = null,
  formatHeaderList = null, hasUnsavedChanges = false, totalRows = -1, isCsv = false, translationColumns, noteDefs = null, isNpi = false, importInfoRows = null, importCheckbox = null }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const fileInputRef = useRef();
  const excelGridApiRef = useRef(null);
  const reDownload = useRef(false);
  const { snackBarMessageColor } = ColorUtil;

  const exportOptions = [
    t("exportAsExcel"),
    t("exportAsCsv")
  ]

  const filterCsvMeasure = (params) => {
    const unusedCsvExportMeasure = CodeUtil.unusedCsvExportMeasure;
    if (params.measures) {
      params.measures = params.measures.filter(measure => !unusedCsvExportMeasure.includes(measure));
      if (params.measures.length === 0) {
        return null;
      }
    }

    return params
  }

  // columnDefs(children 포함)를 순회하며 { translation: 헤더명(번역됨), origin: field } 목록으로 평탄화
  const flattenColumnDefsForTranslation = (defs) => {
    const rows = [];
    (defs || []).forEach(def => {
      if (def.children) {
        rows.push(...flattenColumnDefsForTranslation(def.children));
      } else if (def.field) {
        rows.push({ translation: def.headerName || def.field, origin: def.field });
      }
    });
    return rows;
  };

  const exportAllDataToExcel = (isExport = true, isTranslationKey = false) => {
    setOpen(false);
    // skipRowGroups: Export시 row group 제거.
    // suppressRowOutline: Export시 row outline(collapse) 제거.
    // suppressColumnOutline: Export시 column collapse 제거.
    if (isExport) {
      if (isTranslationKey) {
        const translationRows = flattenColumnDefsForTranslation(columnDefs);
        const preExcelParams = excelGridApiRef.current?.getGridOption?.("defaultExcelExportParams");
        const sheets = [];

        const mainSheet = excelGridApiRef.current.getSheetDataForExcel({
          sheetName: preExcelParams?.sheetName,
          skipRowGroups: true,
          suppressRowOutline: true,
        });
        sheets.push(mainSheet);

        const translationExcelApi = cloneDeep(excelGridApiRef.current);
        translationExcelApi.setGridOption('columnDefs', [
          { field: 'translation', headerName: 'translation' },
          { field: 'origin', headerName: 'origin' },
        ]);
        GridUtils.setRowData(translationExcelApi, translationRows);
        const translationSheet = translationExcelApi.getSheetDataForExcel({
          sheetName: "translationKey",
        });
        sheets.push(translationSheet);

        excelGridApiRef.current.exportMultipleSheetsAsExcel({
          ...preExcelParams,
          data: sheets
        });
      } else {
        excelGridApiRef.current.exportDataAsExcel({
          skipRowGroups: true, suppressRowOutline: true
        });
      }
    }
  }

  const parseCSVLine = (line) => {
    /* 정규식:
     * 빈 필드(Null값)도 데이터로 처리
     * Contents에 따옴표가 포함된 경우,
     * Contents에 ,(comma)가 포함된 경우
    */
    const regex = /(?:^|,)(?:"((?:[^"]|"")*)"|([^",]*))/g;
    const result = [];
    let match = null;

    while ((match = regex.exec(line)) !== null) {
      // 만약 현재 매치된 index와 regex.lastIndex가 같다면(lastIndex가 갱신되지 않았다면)
      // 무한 루프에 빠지는 것을 방지하기 위해 lastIndex를 강제로 1 증가.
      if (match.index === regex.lastIndex) {
        regex.lastIndex++;
      }

      let value = match[1] || match[2]; // 그룹 1(따옴표 안) 없으면 그룹 2(일반 값)
        // 이중 인용부호 ("")를 하나의 "로 변환
        if (value) {
            value = value.replace(/""/g, '"');
        }

        result.push(value);
    }

    return result;
  }

  // Csv Row Join시 Value가 ,를 포함하고 있다면 따옴표로 감싸주기
  const escapeCSVValue = (value) => {
    if (value && value.includes(',')) {
      return `"${value}"`;
    }
    return value;
  }

  const retrieveData = () => {
    if (isNpi) {
      service.getNpiProfileAllForCSV(params)
        .then(response => {
          if (response.data) {
            let isExport = true;
            const { results } = response.data;
            GridUtils.setRowData(excelGridApiRef.current, results);
            reDownload.current = true;
            exportAllDataToExcel(isExport, true);
          }
        })
      .catch(e => {
          setOpen(false);
          debug(e);
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        });
    } else {
      service.getAllForCSV(params)
        .then(response => {
          let isExport = true;
          const { results, pivotResult } = response.data;
          const isHasHeader = response.data?.header;
          if (pivotResult && setExcelData) {
            const { localDateHeader, data } = pivotResult;
            let headers = localDateHeader;
            if (headerFormatter) {
              headers = localDateHeader.map(header => headerFormatter(header));
            }
            const noteData = response?.data?.noteResult;
            if (!isEmpty(noteData) && noteDefs !== null) {
              isExport = false;
              const excelApi = cloneDeep(excelGridApiRef.current);
              const excelColumDefs = excelApi.getColumnDefs();
              const colDefs = excelColumDefs.map(col => { if (col.rowGroup) col.hide = true; return col; });

              excelApi.setGridOption('columnDefs', colDefs);
              GridUtils.setRowData(excelApi, setExcelData(headers, data));
              const preExcelParams = excelApi?.getGridOption?.("defaultExcelExportParams");
              const sheets = [];
              const firstSheet = excelApi?.getSheetDataForExcel({
                sheetName: preExcelParams.sheetName,
                skipRowGroups: true,
                suppressRowOutline: true,
              });
              sheets.push(firstSheet);

              const noteExcelApi = cloneDeep(excelGridApiRef.current);
              noteExcelApi.setGridOption('columnDefs', noteDefs);
              GridUtils.setRowData(noteExcelApi, noteData);
              const noteSheet = noteExcelApi.getSheetDataForExcel({
                sheetName: t("notes"),
              });
              sheets.push(noteSheet);

              excelApi.exportMultipleSheetsAsExcel({
                ...preExcelParams,
                data: sheets
              });
            } else {
              GridUtils.setRowData(excelGridApiRef.current, setExcelData(headers, data));
            }
          } else {
            if (!isNull(dataType) && !isNull(formatHeaderList) && setExcelData) {
              GridUtils.setRowData(excelGridApiRef.current, setExcelData(formatHeaderList, { data: results }, dataType));
            } else if (setExcelData && isHasHeader) {
              GridUtils.setRowData(excelGridApiRef.current, setExcelData(response.data));
            } else if (setExcelData) {
              GridUtils.setRowData(excelGridApiRef.current, setExcelData(results));
            } else {
              GridUtils.setRowData(excelGridApiRef.current, results);
            }
          }

          reDownload.current = true;
          exportAllDataToExcel(isExport);
        })
        .catch(e => {
          setOpen(false);
          debug(e);
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        });
    }
  }

  /* eslint-disable consistent-return */
  const retrieveCsvData = () => {
    const filteredParams = filterCsvMeasure(params);
    if (filteredParams === null) {
      setOpen(false);
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_MEASURE_CSV_EXPORT_NOT_SUPPORTED") });
      return null;
    }
    service.getAllForRawCSV(filteredParams)
      .then(response => {
        if (!response) {
          return null;
        } else {
          if (response.status === 204) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
            return null;
          }
          if (!response.ok) {
            setOpen(false);
            setSnackInfo({
              open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError")
            });
            return null;
          }
          const contentDisposition = response.headers.get('Content-Disposition');
          let fileName = 'export.csv';
          if (contentDisposition && contentDisposition.indexOf('attachment; filename=') !== -1) {
            fileName = contentDisposition.split('attachment; filename=')[1].trim();
          }
          return response.blob()
            .then(blob => {
              if (blob) {
                return blob.text().then(text => ({ text, fileName }));
              } else {
                return null;
              }
            });
        }
      })
      .then(({ text, fileName }) => {
        const lines = text.split('\n');
        const originalHeader = lines[0].split(',');
        const headerMap = {};
        originalHeader.forEach(header => {
          const trimmedHeader = header.trim();
          try {
            headerMap[trimmedHeader] = t(trimmedHeader) || trimmedHeader;
          } catch (error) {
            headerMap[trimmedHeader] = trimmedHeader;
          }
        });
        const newHeader = Object.values(headerMap).join(',');

        const translationKeys = Array.isArray(translationColumns) ? translationColumns : [];

        const dataRows = lines.slice(1).map(line => {
          const rowValues = parseCSVLine(line);

          if (translationKeys.includes("measure")) {
            const measureIndex = originalHeader.findIndex(header => header.trim() === "measure");
            const measureValue = measureIndex !== -1 ? rowValues[measureIndex] : null;
            if (measureValue) {
              rowValues[measureIndex] = t(measureValue.trim()) || measureValue.trim();
            }
          }

          if (translationKeys.includes("orderCalendar")) {
            const calendarIndex = originalHeader.findIndex(header => header.trim() === "orderCalendar");
            const calendarValue = calendarIndex !== -1 ? rowValues[calendarIndex] : null;
            if (calendarValue === "notConfigured") {
              rowValues[calendarIndex] = t(calendarValue.trim()) || calendarValue.trim();
            }
          }

          if (translationKeys.includes("orderMethod")) {
            const orderMethodIndex = originalHeader.findIndex(header => header.trim() === "orderMethod");
            const orderMethodValue = orderMethodIndex !== -1 ? rowValues[orderMethodIndex] : null;
            if (orderMethodValue) {
              rowValues[orderMethodIndex] = t(orderMethodValue.trim()) || orderMethodValue.trim();
            }
          }

          if (translationKeys.includes("forecastMethod")) {
            const forecastMethodIndex = originalHeader.findIndex(header => header.trim() === "forecastMethod");
            const forecastMethodValue = forecastMethodIndex !== -1 ? rowValues[forecastMethodIndex] : null;
            if (forecastMethodValue) {
              const algorithm = Object.values(CodeUtil.bfAlgorithm).find(
                (algo) => algo.cd === forecastMethodValue.trim()
              );
              if (algorithm) {
                rowValues[forecastMethodIndex] = algorithm.name || forecastMethodValue.trim();
              }
            }
          }

          if (translationKeys.includes("itemType")) {
            const itemTypeIndex = originalHeader.findIndex(header => header.trim() === "itemType");
            const itemTypeValue = itemTypeIndex !== -1 ? rowValues[itemTypeIndex] : null;

            if (itemTypeValue && itemTypeValue.trim()) {
              const normalizedValue = itemTypeValue.toLowerCase().trim();
              rowValues[itemTypeIndex] = t(normalizedValue) || itemTypeValue.trim();
            }
          }

          return rowValues.map(escapeCSVValue).join(',');
        });

        const updatedCsv = `\uFEFF${[newHeader, ...dataRows].join('\n')}`;
        const updatedBlob = new Blob([updatedCsv], { type: 'text/csv' });
        const link = document.createElement('a');
        setOpen(false);
        link.href = window.URL.createObjectURL(updatedBlob);
        link.download = fileName;
        link.click();
      })
      .catch(e => {
        setOpen(false);
        debug(e);
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.networkError,
          content: t("MSG_NetworkError")
        });
      });
  }

  const handleClick = (event) => {
    if (isCsv) {
      setAnchorEl(event.currentTarget);
    } else {
      let content = '';
      let hasWarn = false;

      if (hasUnsavedChanges) {
        hasWarn = true;
        content += `${t("MSG_UnsavedChanges")} ${t("MSG_DownloadWithoutChanges")}`;
      }

      const excelLimit = isEqual(title, "menuDpPlanVsActual") ? DP_PLAN_VS_ACTUAL_EXPORT_LIMIT : CSV_DOWNLOAD_MAX_SIZE;
      if (totalRows > excelLimit) {
        hasWarn = true;
        content += isEmpty(content) ? `${t("MSG_ExportLimit")}` : `\n ${t("MSG_ExportLimit")}`;
      }

      if (hasWarn) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.exportLimit, content });
      }

      setOpen(true);
      reDownload.current ? exportAllDataToExcel() : retrieveData();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const importFile = () => {
    setImportConfirmOpen(true);
  };

  const openFilePicker = () => {
    fileInputRef.current.value = "";
    const fileInput = document.querySelector("#btnImportFile");
    fileInput.click();
  };

  const handleImportConfirm = () => {
    setImportConfirmOpen(false);
    openFilePicker();
  };

  /*
   * Import 확인 모달 본문 — "무엇을 가져오는가"(범위)와 "어떻게 가져오는가"(옵션)를 나눠 보여준다.
   * 예전에는 "- Import Info" 불릿 아래 `라벨: 값` 문장을 회색으로 죽 늘어놓아서
   * 어디가 값이고 어디가 안내문인지 구분이 안 됐다. 값을 라벨/값 2열 그리드로 세우고
   * 안내문은 셸의 subtitle 로 올렸다.
   */
  const renderImportConfirmBody = () => {
    const importCheckboxList = (Array.isArray(importCheckbox) ? importCheckbox : [importCheckbox]).filter(Boolean);
    const startDateStr = params?.startDate ? dateUtils.formatDate(params.startDate, DEFAULT_DATE_FORMAT) : "";
    const endDateStr = params?.endDate ? dateUtils.formatDate(params.endDate, DEFAULT_DATE_FORMAT) : "";
    const scopeRows = [
      { label: t("period"), value: `${startDateStr} ~ ${endDateStr}` },
      { label: t("custLevel"), value: params?.custLevelCd ?? "" },
      { label: t("itemLevel"), value: params?.itemLevelCd ?? "" },
      ...(importInfoRows ?? []),
    ];

    return (
      <Box>
        <Box sx={{
          display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 2, rowGap: 1,
          bgcolor: "grey.50", border: 1, borderColor: "divider", borderRadius: 1, px: 1.5, py: 1.25,
        }}>
          {scopeRows.map(row => (
            <Fragment key={row.label}>
              <Box sx={{ fontSize: FS.SUB, color: "text.secondary", lineHeight: 1.4 }}>{row.label}</Box>
              <Box sx={{ fontSize: FS.TITLE, fontWeight: 600, lineHeight: 1.4, wordBreak: "break-word" }}>{row.value}</Box>
            </Fragment>
          ))}
        </Box>
        {importCheckboxList.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ ...MODAL_SECTION_LABEL_SX, pb: 1 }}>{t("importOption")}</Box>
            {importCheckboxList.map(checkbox => (
              <FormControlLabel
                key={checkbox.label}
                sx={{ display: "flex", ml: -0.75, "& .MuiFormControlLabel-label": { fontSize: FS.TITLE } }}
                control={
                  <Checkbox
                    size="small"
                    checked={checkbox.checked}
                    onChange={event => checkbox.onChange(event.target.checked)}
                  />
                }
                label={checkbox.label}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // import
  const selectFile = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);

      fileReader.onload = function () {
        setOpen(true);
        handleImportForEdit(file);
        }
      }
      fileInputRef.current.value = "";
  }

  const handleImportForEdit = (file) => {
    const formData = new FormData();
    formData.append('file', file);  // Excel 파일
    if (params) {
      for (const key in params) {
        const value = params[key] instanceof Date
          ? dateUtils.formatDate(params[key], DEFAULT_DATE_FORMAT)
          : params[key];
        formData.append(key, value);
      }
    }

    service.importAll(formData)
      .then(response => {
        if (response?.data?.message) {
          setOpen(false);
          setSnackInfo({
            open: true,
            severity: snackBarMessageColor.inputError,
            content: t(response?.data?.message?.message)
          });
          return null;
        }
        setOpen(false)
      })
      .catch(e => {
        debug(e);
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.networkError,
          content: e.message
        });
        setOpen(false)
      });
    setOpen(false);
    setSnackInfo({
      open: true,
      severity: snackBarMessageColor.success,
      content: t("MSG_TaskCompleteAlert")
    });
    window.dispatchEvent(new CustomEvent('export-started'));

  }

  const exportData = (_event, index) => {
    setOpen(true);
    setAnchorEl(null);
    if (index === 0) {
      let content = '';
      let hasWarn = false;

      if (hasUnsavedChanges) {
        hasWarn = true;
        content += `${t("MSG_UnsavedChanges")} ${t("MSG_DownloadWithoutChanges")}`;
      }

      const excelLimit = isEqual(title, "menuDpPlanVsActual") ? DP_PLAN_VS_ACTUAL_EXPORT_LIMIT : CSV_DOWNLOAD_MAX_SIZE;
      if (totalRows > excelLimit) {
        hasWarn = true;
        content += isEmpty(content) ? `${t("MSG_ExportLimit")}` : `\n ${t("MSG_ExportLimit")}`;
      }

      if (hasWarn) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.exportLimit, content });
      }

      setOpen(true);
      reDownload.current ? exportAllDataToExcel() : retrieveData();

    } else if (index === 1) {
      setOpen(true);
      retrieveCsvData();
    }
  };
  const excelInit = (rootApi) => {
    excelGridApiRef.current = rootApi.api;
    setLoading(true);
  }

  const defaultGridSettingMemo = useMemo(() => {
    return DefaultGridSetting({ title });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) return;

    reDownload.current = false;

    if (!isNull(excelGridApiRef.current) && columnDefs) {
      excelGridApiRef.current.setGridOption('columnDefs', []);
      excelGridApiRef.current.setGridOption('columnDefs', GridUtils.getColumnDefs(columnDefs));
    }

    excelGridApiRef.current.getColumns()?.forEach(column => {
      const colId = column?.colId
      const groupId = column?.parent?.groupId;

      if (colId) {
        let visible = true;
        if (column?.colDef && Object.prototype.hasOwnProperty.call(column.colDef, "rowGroup") && column.colDef.rowGroup) {
          visible = false;
        }

        if (column?.colDef?.cellRenderer && column.colDef.cellRenderer === "agGroupCellRenderer" && column.colDef.hide) {
          visible = false;
        }

        excelGridApiRef.current.setColumnsVisible([colId], visible);
      }
      if (groupId) {
        excelGridApiRef.current.setColumnGroupOpened(groupId, true);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, columnDefs, title, params]);

  return (
    <Box sx={{ display: "inline-flex", position: "relative", verticalAlign: "middle" }}>
      <DownloadButton label="exportAll" onClick={handleClick} />
      {
        isNpi ?
          <UploadButton aria-label="import" onClick={importFile} /> : null
      }

      <input ref={fileInputRef} hidden id="btnImportFile" type="file" onChange={selectFile} />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={open}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {isCsv && (
        <Menu
          id="lock-menu"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'lock-button',
            role: 'listbox',
            dense: true,
          }}
        >
          {exportOptions.map((option, index) => (
            <MenuItem
              key={option}
              onClick={(event) => exportData(event, index)}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      )}
      <div className="ag-theme-balham" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', visibility: 'hidden' }}>
        <AgGridReact
          {...defaultGridSettingMemo}
          onGridReady={excelInit}
          suppressAggFuncInHeader
          groupDisplayType={'custom'}
        />
      </div>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      {isNpi && (
        /* 확인 박스가 아니라 "가져올 범위 + 옵션"을 보여 주는 설정 모달이라 셸을 쓴다.
           주 버튼은 저장이 아니라 파일 선택기를 여는 동작이므로 라벨/아이콘을 갈아끼웠다. */
        <BaseConfigModal
          open={importConfirmOpen}
          onClose={() => setImportConfirmOpen(false)}
          title={t("import")}
          subtitle={t("MSG_NpiImportConfirm", { import: t("import") })}
          width={440}
          saveLabel={t("chooseFile")}
          saveIcon={<UploadFileIcon />}
          onSave={handleImportConfirm}
        >
          {renderImportConfirmBody()}
        </BaseConfigModal>
      )}
    </Box>
  );
};

export default withTranslation()(ExcelExportButton); 